import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { CurrencyPicker, DialogForm, UserPicker } from '../../../shared/components/common';
import { CompanyCreateField, useCompanyCreatePrecedence } from '../../../shared/components/multi-company';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';
import type { PendingLineItem } from './InvoiceLineEditor';
import { OrganizationPicker } from '../../master_data/contacts/components/OrganizationPicker';
import { ContactPicker } from '../../master_data/contacts/components/ContactPicker';
import { useAccountingTenantSettings } from '../hooks/useSettings';
import { useTenantLocaleSettings } from '../../../shared/hooks/useTenantSettings';
import { useRevenueCategoryRules, useCreateRevenueCategoryRule } from '../hooks/useRevenueCategoryRules';
import { aiApi } from '../services/accountingApi';
import type { InvoiceCreate, AccountSuggestion } from '../types';
import { toDateOnlyString } from '@/shared/utils/dateFormat';

interface InvoiceQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: InvoiceCreate) => void;
  isLoading?: boolean;
  prefill?: {
    customer_name?: string;
    issue_date?: string;
    due_date?: string;
    reference?: string;
    notes?: string;
    lines?: PendingLineItem[];
  };
}

export function InvoiceQuickCreateDialog({ open, onClose, onSubmit, isLoading, prefill }: InvoiceQuickCreateDialogProps) {
  const { t } = useTranslation('accounting');
  const { data: tenantSettings } = useAccountingTenantSettings();
  const { data: localeSettings } = useTenantLocaleSettings();
  const { data: rulesData } = useRevenueCategoryRules();
  const createRule = useCreateRevenueCategoryRule();

  const { multiCompany, defaultCompanyId } = useCompanyCreatePrecedence();
  const [companyId, setCompanyId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [contactId, setContactId] = useState('');
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [issueDate, setIssueDate] = useState(toDateOnlyString(new Date()));
  const [dueDate, setDueDate] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [parsedLines, setParsedLines] = useState<PendingLineItem[]>([]);
  const [invoiceLevelAccountId, setInvoiceLevelAccountId] = useState('');
  const [currencyCode, setCurrencyCode] = useState<string | null>(null);
  const [aiSuggestion, setAiSuggestion] = useState<(AccountSuggestion & { organizationId: string }) | null>(null);


  useEffect(() => {
    if (open) {
      // Company precedence: pre-fill the specific global scope; empty (= force
      // pick via the required picker) when scope is "All companies".
      setCompanyId(defaultCompanyId);
      setCustomerId('');
      setCustomerName(prefill?.customer_name || '');
      setContactId('');
      setEmployeeId(null);
      setCurrencyCode(localeSettings?.default_currency || null);
      setAiSuggestion(null);
      setInvoiceLevelAccountId(tenantSettings?.default_sales_account_id || '');
      const today = toDateOnlyString(new Date());
      setIssueDate(prefill?.issue_date || today);
      const due = new Date();
      due.setDate(due.getDate() + 30);
      setDueDate(prefill?.due_date || toDateOnlyString(due));
      setReference(prefill?.reference || '');
      setNotes(prefill?.notes || '');
      setParsedLines(prefill?.lines || []);
    }
  }, [open, prefill, defaultCompanyId]);

  const revenueCategoryRules = rulesData?.data || [];

  const handleSubmit = () => {
    if (!dueDate) return;
    // Multi-company guard: when scope is "All companies" the required picker
    // stays empty and we block submit until the user picks. In a specific
    // scope the chip pre-fills companyId (one-click happy path); single-company
    // tenants render nothing and the backend defaults to the only company.
    if (multiCompany && !companyId) return;

    // Include any parsed lines from document upload
    const defaultAccountId = invoiceLevelAccountId || tenantSettings?.default_sales_account_id || '';
    const resolvedLines = parsedLines
      .filter((l) => l.description && l.unit_price > 0)
      .map((l) => ({
        description: l.description,
        account_id: l.account_id || defaultAccountId,
        offering_id: l.offering_id || undefined,
        quantity: l.quantity,
        unit_price: l.unit_price,
        discount_percent: l.discount_percent,
        tax_rate_id: l.tax_rate_id || undefined,
        tax_amount: l.tax_amount,
      }));

    onSubmit({
      company_id: companyId || undefined,
      customer_id: customerId || undefined,
      customer_name: customerName || undefined,
      contact_id: contactId || undefined,
      employee_id: employeeId || undefined,
      currency_code: currencyCode || undefined,
      issue_date: issueDate,
      due_date: dueDate,
      reference: reference || undefined,
      notes: notes || undefined,
      lines: resolvedLines.length > 0 ? resolvedLines : undefined,
    });
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={t('invoices.createInvoice')}
      submitLabel={t('invoices.createInvoice')}
      isLoading={isLoading}
      maxWidth="lg"
    >
      <div className="space-y-4">
        <CompanyCreateField value={companyId} onChange={setCompanyId} />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('invoices.customer')}</Label>
            <OrganizationPicker
              value={customerId}
              onChange={(id) => {
                setCustomerId(id);
                setContactId('');
              }}
              onCompanySelected={(company) => {
                setCustomerName(company?.name ?? '');
                setAiSuggestion(null);
                // Auto-fill currency from customer's default (user can override)
                if (company?.default_currency_code) {
                  setCurrencyCode(company.default_currency_code);
                } else {
                  setCurrencyCode(localeSettings?.default_currency || null);
                }
                // Check for customer revenue category rule first
                const customerRule = company ? revenueCategoryRules.find(
                  (r) => r.entity_type === 'customer' && r.entity_id === company.id
                ) : undefined;
                if (customerRule) {
                  setInvoiceLevelAccountId(customerRule.account_id);
                } else if (company) {
                  // AI suggestion as last resort
                  aiApi.suggestAccount(company.name, 'revenue', company.industry ?? undefined).then((res) => {
                    const suggestion = res.data;
                    if (suggestion.account_id) {
                      setAiSuggestion({ ...suggestion, organizationId: company.id });
                      setInvoiceLevelAccountId(suggestion.account_id);
                    }
                  }).catch(() => { /* Silently fail — user can pick manually */ });
                }
              }}
              organizationRelationshipFilter="customer"
              placeholder={t('invoices.customerPlaceholder')}
              addNewLabel={t('contacts:companyPicker.addNewCustomer')}
              defaultRelationships={['customer']}
              allowClear
            />
          </div>
          <div className="space-y-2">
            <ContactPicker
              value={contactId}
              onChange={setContactId}
              organizationId={customerId || undefined}
              label={t('invoices.contact')}
              placeholder={t('invoices.contactPlaceholder')}
              disabled={!customerId}
              defaultCompanyId={customerId || undefined}
              allowClear
            />
          </div>
        </div>

        {aiSuggestion && (
          <div className="flex items-center gap-2 p-2 bg-primary/5 rounded text-sm">
            <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
            <span className="flex-1">{t('invoices.aiSuggestedAccount', { account: aiSuggestion.account_name })}</span>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={() => {
                if (aiSuggestion.account_id) createRule.mutate({ account_id: aiSuggestion.account_id, entity_type: 'customer', entity_id: aiSuggestion.organizationId });
                setAiSuggestion(null);
              }}
            >
              {t('common:app.confirm')}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAiSuggestion(null)}>
              {t('common:app.dismiss')}
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('invoices.currency')}</Label>
            <CurrencyPicker
              value={currencyCode}
              onChange={setCurrencyCode}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('invoices.reference')}</Label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder={t('invoices.referencePlaceholder')}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t('invoices.employee')}</Label>
          <UserPicker
            value={employeeId}
            onChange={setEmployeeId}
            placeholder={t('invoices.employeePlaceholder')}
            filterUserTypes={['internal']}
            size="sm"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('invoices.issueDate')}</Label>
            <Input
              type="date"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('invoices.dueDate')}</Label>
            <Input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t('invoices.notes')}</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('invoices.notesPlaceholder')}
            rows={2}
          />
        </div>
      </div>
    </DialogForm>
  );
}
